import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { APPOINTMENT_STATUS, APPT_STATUS_ROLES, appointmentStatusOptionsFor, SERVICES, cardColors, formatDateTime, formatDate, formatTime } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, CalendarDays, List as ListIcon, ChevronLeft, ChevronRight, ChevronDown, Clock, Armchair, LogIn, Stethoscope, CheckCircle2, X, FileText, User, Pencil, ArrowUp, ArrowDown, ArrowUpDown, Receipt, SlidersHorizontal, Search, Phone, Play, Square, Download } from "lucide-react";
import { TopbarPortal } from "@/components/crm/Topbar";
import ColumnChooserDialog from "@/components/crm/ColumnChooserDialog";
import PatientAvatar from "@/components/crm/PatientAvatar";
import VisitedFields, { visitedPayload } from "@/components/crm/VisitedFields";
import { AvailabilityDialog, unavailableRanges, dayHours } from "@/components/crm/Availability";
import { LeadSearchInput } from "@/components/crm/LeadSearchInput";
import { Badge } from "@/components/ui/badge";

// Grid resolution for the day calendar. Configurable from settings later.
const SLOT_MINUTES = 15;
// Outer bounds the grid will never exceed, and the fallback window used when no
// availability is known. The actual visible window fits the day's working hours.
const MIN_HOUR = 7;         // 7:00 AM
const MAX_HOUR = 22;        // 10:00 PM
const FALLBACK_START = 9;   // used when a doctor has no availability set
const FALLBACK_END = 18;
const PX_PER_MIN = 3.2;     // vertical scale (60min = 192px, 15min = 48px)

// Card accent per status, drawn from the brand palette (navy / gold / cream /
// nude). Lives in lib/constants.js — CalendarPage.jsx shares it.

// Full-row background tint for the appointments list, by status. Cancelled /
// no-show read as red (won't happen), visited as green (done AND paid).
// `checked_out` is deliberately NOT tinted — the visit isn't finished until
// reception has taken the money, so it must not look done.
// `bg` + a matching `hover:` so the tint survives the hover state.
const APPT_ROW_TINT = {
  cancelled: "bg-red-100 hover:bg-red-200",
  no_show:   "bg-red-100 hover:bg-red-200",
  visited:   "bg-green-100 hover:bg-green-200",
};

function StatusBadge({ status }) {
  const s = APPOINTMENT_STATUS.find((x) => x.value === status) || APPOINTMENT_STATUS[0];
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.color}`}>{s.label}</span>;
}

// Multi-value filters (status/doctor/branch/service) are arrays: empty = "all".
// Two independent date ranges: date_* filters on the appointment slot
// (scheduled_at), booked_* on when it was booked (created_at). Both can be
// active at once — e.g. "August appointments that were booked in July".
const EMPTY_FILTERS = {
  search: "", status: [], doctor_id: [], branch_id: [], service: [],
  date_from: "", date_to: "", booked_from: "", booked_to: "",
};

// Shift a YYYY-MM-DD by n days, staying on the local calendar.
const shiftDay = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Quick ranges for the toolbar. "All" clears the window — the server then
// returns the most RECENT appointments rather than the oldest.
const DATE_PRESETS = [
  { label: "Today", range: () => ({ from: todayISO(), to: todayISO() }) },
  { label: "Tomorrow", range: () => ({ from: shiftDay(todayISO(), 1), to: shiftDay(todayISO(), 1) }) },
  { label: "Next 7 days", range: () => ({ from: todayISO(), to: shiftDay(todayISO(), 6) }) },
  { label: "All", range: () => ({ from: "", to: "" }) },
];

// Local (IST for our users) calendar day of an ISO timestamp, as YYYY-MM-DD so
// it compares directly against an <input type="date"> value. Returns "" when the
// timestamp is missing or unparseable (legacy import dates).
const localDateKey = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const todayISO = () => localDateKey(new Date().toISOString());

export default function Appointments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDoctor = user?.role === "doctor";
  // Telecallers must not see appointment revenue (sensitive billing figure).
  // All roles including telecallers may see/enter revenue (business decision
  // 2026-07-20). Re-gate here (plus the backend strip in list_appointments and
  // the app's AppUser.canViewRevenue) if that ever changes.
  const canViewRevenue = true;
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null); // {doctor_id, scheduled_at} for empty-slot booking
  const [editing, setEditing] = useState(null); // appointment being viewed/edited
  const [view, setView] = useState("list"); // list | waiting
  const [availability, setAvailability] = useState({}); // doctorId -> availability
  const [availEditing, setAvailEditing] = useState(null); // {id, name} being edited
  const [waiting, setWaiting] = useState([]); // waiting-room appointments
  // "How many people are waiting on the FRONT DESK" — those checked in and
  // those at the desk for checkout. A patient in consultation is the doctor's
  // problem, not reception's, so they don't count toward the badge.
  const waitingCount = waiting.filter((a) => a.status !== "in_consultation").length;

  const openNew = (pf = null) => { setPrefill(pf); setOpen(true); };
  const [calDate, setCalDate] = useState(todayISO());
  // Opens on TODAY. A front desk wants today's schedule, not the entire
  // history — and the server caps the list, so an unfiltered load used to
  // return the oldest appointments and push today off the end entirely.
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, date_from: todayISO(), date_to: todayISO() });
  const [statusChange, setStatusChange] = useState(null); // {apptId, newStatus, apptName}
  const [rescheduling, setRescheduling] = useState(null); // appointment being rescheduled
  const [visiting, setVisiting] = useState(null);         // appointment being marked visited (capture revenue)
  const [viewingCalls, setViewingCalls] = useState(null); // appointment whose call history is open

  // The date window is applied SERVER-side (everything else stays client-side),
  // so the row cap only ever bites on the range you actually asked for.
  const { date_from: dateFrom, date_to: dateTo } = filters;
  const load = useCallback(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/appointments", { params }).then((r) => setItems(r.data)).catch(() => {});
    api.get("/appointments/waiting").then((r) => setWaiting(r.data)).catch(() => {});
  }, [dateFrom, dateTo]);
  // This board is a hand-off between reception and the doctor at two different
  // screens, so it has to update itself. It previously had no live subscription
  // at all — the only way to see a check-in or a completed consultation was to
  // reload the tab.
  useLiveEvents((e) => {
    if (e?.type === "appointment.changed" || e?.type === "invoice.payment") load();
  });

  useEffect(() => {
    load();
    // A doctor with no branch assignment sees only their own column. A doctor
    // WITH branches also gets the other doctors of those branches — the API now
    // returns their branches' whole calendar (_scope_appointment_query), and
    // without the extra columns those appointments have nowhere to render.
    const myBranchIds = user?.branch_ids || [];
    if (isDoctor && !myBranchIds.length) {
      setDoctors(user ? [user] : []);
    } else {
      const docParams = { role: "doctor" };
      api.get("/users", { params: docParams }).then((r) => {
        const list = isDoctor
          ? r.data.filter((d) => d.id === user?.id
              || (d.branch_ids || []).some((b) => myBranchIds.includes(b)))
          : r.data;
        setDoctors(list);
      }).catch(() => {});
    }
    api.get("/branches").then((r) => {
      let list = r.data;
      // Narrow the branch picker to the user's own branches for EVERY
      // branch-assigned role, not just managers/reception — a telecaller or
      // doctor with branches set is scoped to them server-side
      // (_scope_appointment_query), so offering the rest is a filter that
      // silently returns nothing. Admins and franchise owners aren't scoped by
      // branch_ids and keep the full list.
      const branchScoped = !["super_admin", "hq_admin", "franchisee_owner"].includes(user?.role);
      if (branchScoped && user?.branch_ids?.length) {
        list = list.filter((b) => (user.branch_ids || []).includes(b.id));
      }
      setBranches(list);
    }).catch(() => {});
  }, [load, isDoctor, user]);

  // Load each visible doctor's availability for calendar shading.
  const loadAvailability = useCallback(() => {
    doctors.forEach((d) => {
      api.get(`/users/${d.id}/availability`)
        .then((r) => setAvailability((m) => ({ ...m, [d.id]: r.data })))
        .catch(() => {});
    });
  }, [doctors]);
  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const requestStatusChange = (apptId, newStatus, apptName = "") => {
    // Check-in is never a bare status flip any more — it's a full screen where
    // reception corrects the patient's details, assigns the doctor, takes a
    // photo and records vitals.
    if (newStatus === "checked_in") { navigate(`/checkin/${apptId}`); return; }
    setStatusChange({ apptId, newStatus, apptName });
  };

  const confirmStatusChange = async (note) => {
    if (!statusChange) return;
    try {
      await api.put(`/appointments/${statusChange.apptId}`, {
        status: statusChange.newStatus,
        status_note: note || "",
      });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setStatusChange(null);
  };

  const updateStatus = (id, status, name) => requestStatusChange(id, status, name);

  const startConsultation = (apptId) => navigate(`/consultation/${apptId}`);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  // Both ends in one update — setting them separately fires two server loads.
  const setDateRange = (from, to) => setFilters((f) => ({ ...f, date_from: from, date_to: to }));
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const hasActiveFilters = Object.entries(filters).some(([, v]) =>
    Array.isArray(v) ? v.length > 0 : v !== ""
  );

  const docName = (id) => doctors.find((d) => d.id === id)?.name || "—";
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const filtered = items.filter((a) => {
    if (filters.search) {
      const q = filters.search.toLowerCase().trim();
      // Numbers are matched on digits only: "90040 71257", "+91 9004071257"
      // and "9004071257" are the same patient, but a raw substring match
      // failed on the space and showed "0 appointments". Past 10 digits the
      // extra leading ones are a country code, so keep the last 10.
      const qDigits = q.replace(/\D/g, "").slice(-10);
      const byName = a.patient_name?.toLowerCase().includes(q);
      const byMobile = qDigits.length >= 3 && (a.mobile || "").replace(/\D/g, "").includes(qDigits);
      if (!byName && !byMobile) return false;
    }
    if (filters.status.length && !filters.status.includes(a.status)) return false;
    if (filters.doctor_id.length && !filters.doctor_id.includes(a.doctor_id)) return false;
    if (filters.branch_id.length && !filters.branch_id.includes(a.branch_id)) return false;
    if (filters.service.length && !filters.service.includes(a.service)) return false;
    if (filters.date_from || filters.date_to) {
      const apptDate = localDateKey(a.scheduled_at);
      if (!apptDate) return false;
      if (filters.date_from && apptDate < filters.date_from) return false;
      if (filters.date_to && apptDate > filters.date_to) return false;
    }
    if (filters.booked_from || filters.booked_to) {
      const bookedDate = localDateKey(a.created_at);
      if (!bookedDate) return false;   // no booking stamp can't match a range
      if (filters.booked_from && bookedDate < filters.booked_from) return false;
      if (filters.booked_to && bookedDate > filters.booked_to) return false;
    }
    return true;
  });

  // Page-level actions. Sized to match the compact filter row, since that's
  // where they render in list view.
  const pageActions = (
    <>
      {isDoctor && user && (
        <Button variant="outline" onClick={() => setAvailEditing({ id: user.id, name: user.name })} className="h-7 border-navy/15 px-2 text-xs font-medium text-navy hover:bg-navy/5" data-testid="my-availability-btn">
          <Clock className="mr-1 h-3.5 w-3.5" /> My Availability
        </Button>
      )}
      <Button onClick={() => navigate("/appointments/book")} className="h-7 bg-navy px-2 text-xs font-medium text-white hover:bg-navy-hover" data-testid="new-appointment-btn">
        <Plus className="mr-1 h-3.5 w-3.5" /> Book Appointment
      </Button>
    </>
  );

  return (
    // Bound the page to the viewport so the list's table scrolls internally and
    // there's no dead space below it (the toolbar is a fixed row; the active view
    // fills the rest). The calendar/waiting views size themselves naturally.
    <div className={view === "list" ? "flex h-[calc(100vh-4rem-4rem)] flex-col gap-4" : "space-y-5"}>
      {/* View switcher sits beside the "Appointments" page title. */}
      <TopbarPortal slot="title">
        <div className="inline-flex rounded-lg border border-navy/10 bg-white p-0.5">
          <button
            onClick={() => setView("list")}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "list" ? "bg-navy text-white" : "text-muted-foreground hover:text-ink"}`}
            data-testid="appt-view-list"
          >
            <ListIcon className="h-4 w-4" /> List
          </button>
          <button
            onClick={() => setView("waiting")}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "waiting" ? "bg-navy text-white" : "text-muted-foreground hover:text-ink"}`}
            data-testid="appt-view-waiting"
          >
            <Armchair className="h-4 w-4" /> Waiting
            {waitingCount > 0 && (
              <span className={`ml-0.5 rounded-full px-1.5 text-[10px] font-bold ${view === "waiting" ? "bg-white/20 text-white" : "bg-gold text-white"}`}>{waitingCount}</span>
            )}
          </button>
        </div>
      </TopbarPortal>

      {/* Search sits in the top header before the bell, like the Leads page.
          Only in list view — the waiting board isn't searchable. */}
      {view === "list" && (
        <TopbarPortal>
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search patient / mobile…"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              className="h-9 bg-white pl-9"
              data-testid="appt-filter-search"
            />
          </div>
        </TopbarPortal>
      )}

      {/* The waiting board has no filter row, so it keeps a row of its own.
          In list view these ride along at the end of the filters (ListView's
          `actions` prop) instead of costing a second row. */}
      {view !== "list" && (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
          {pageActions}
        </div>
      )}

      {view === "list" && (
        <ListView
          actions={pageActions}
          filters={filters}
          setFilter={setFilter}
          setDateRange={setDateRange}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          doctors={doctors}
          branches={branches}
          filtered={filtered}
          items={items}
          docName={docName}
          branchName={branchName}
          updateStatus={updateStatus}
          onSelect={setEditing}
          onReschedule={setRescheduling}
          onVisited={setVisiting}
          onStartConsultation={startConsultation}
          onOpenLead={(leadId) => navigate(`/leads/${leadId}`)}
          onViewCalls={setViewingCalls}
          userRole={user?.role}
          canViewRevenue={canViewRevenue}
        />
      )}
      {view === "waiting" && (
        <WaitingRoom waiting={waiting} docName={docName} updateStatus={updateStatus} onSelect={setEditing} onStartConsultation={startConsultation} onVisited={setVisiting} canViewRevenue={canViewRevenue} userRole={user?.role} onRefresh={load} />
      )}

      <CreateAppointment open={open} onOpenChange={setOpen} prefill={prefill} doctors={doctors} branches={branches} onDone={load} canViewRevenue={canViewRevenue} />
      <EditAppointment appt={editing} onClose={() => setEditing(null)} doctors={doctors} branches={branches} onDone={load} onStartConsultation={startConsultation} canViewRevenue={canViewRevenue} userRole={user?.role} />
      <AvailabilityDialog
        open={!!availEditing}
        userId={availEditing?.id}
        userName={availEditing?.name}
        onClose={() => setAvailEditing(null)}
        onSaved={loadAvailability}
      />
      <StatusChangeDialog
        data={statusChange}
        onClose={() => setStatusChange(null)}
        onConfirm={confirmStatusChange}
      />
      <RescheduleDialog
        appt={rescheduling}
        onClose={() => setRescheduling(null)}
        onDone={load}
      />
      {viewingCalls && (
        <AppointmentCallsDialog
          appt={viewingCalls}
          onClose={() => setViewingCalls(null)}
        />
      )}
      <VisitedDialog
        appt={visiting}
        onClose={() => setVisiting(null)}
        onDone={load}
      />
    </div>
  );
}

/* ───────────────────────── Doctor month calendar ───────────────────────── */

function DoctorMonthCalendar({ date, setDate, appointments, onSelect, onNew, onStartConsultation, updateStatus }) {
  const [selectedDay, setSelectedDay] = useState(date);

  // Parse the current month from `date` (YYYY-MM-DD)
  const viewYear = parseInt(date.slice(0, 4), 10);
  const viewMonth = parseInt(date.slice(5, 7), 10) - 1; // 0-indexed

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    setDate(`${yyyy}-${mm}-01`);
  };

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const weeks = [];
  let week = Array(firstDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }

  // Count appointments per day
  const apptsByDay = {};
  appointments.forEach((a) => {
    const aDate = (a.scheduled_at || "").slice(0, 10);
    if (!aDate) return;
    apptsByDay[aDate] = (apptsByDay[aDate] || 0) + 1;
  });

  const todayStr = todayISO();
  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  // Appointments for selected day
  const dayAppts = appointments
    .filter((a) => (a.scheduled_at || "").slice(0, 10) === selectedDay)
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const h = d.getHours();
    const hr = h % 12 === 0 ? 12 : h % 12;
    const ampm = h < 12 ? "AM" : "PM";
    return `${hr}:${String(d.getMinutes()).padStart(2, "0")} ${ampm}`;
  };

  const statusBadge = (status) => {
    const s = APPOINTMENT_STATUS.find((x) => x.value === status) || APPOINTMENT_STATUS[0];
    return <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.color}`}>{s.label}</span>;
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {/* Monthly grid */}
      <div className="lg:col-span-2 rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        {/* Month header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold text-navy">{monthLabel}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" className="h-8 text-xs" onClick={() => { setDate(todayISO()); setSelectedDay(todayISO()); }}>Today</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 text-center text-xs font-semibold text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="py-2">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {week.map((day, di) => {
              if (!day) return <div key={di} className="h-20 border-t border-navy/5" />;
              const dayStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const count = apptsByDay[dayStr] || 0;
              const isToday = dayStr === todayStr;
              const isSelected = dayStr === selectedDay;

              return (
                <button
                  key={di}
                  onClick={() => setSelectedDay(dayStr)}
                  className={`h-20 border-t border-navy/5 p-1.5 text-left transition-colors hover:bg-nude/30 ${isSelected ? "bg-gold/10 ring-1 ring-gold/30" : ""}`}
                >
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? "bg-navy text-white" : "text-ink"}`}>
                    {day}
                  </span>
                  {count > 0 && (
                    <div className="mt-1">
                      <span className="inline-flex items-center rounded-md bg-navy/10 px-1.5 py-0.5 text-[10px] font-semibold text-navy">
                        {count} appt{count > 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Selected day panel */}
      <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gold">
              {new Date(selectedDay + "T00:00:00").toLocaleDateString(undefined, { weekday: "long" })}
            </p>
            <h4 className="font-display text-lg font-semibold text-navy">
              {new Date(selectedDay + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "long" })}
            </h4>
          </div>
          <span className="text-xs text-muted-foreground">{dayAppts.length} appt{dayAppts.length !== 1 ? "s" : ""}</span>
        </div>

        {dayAppts.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No appointments this day.</p>
        ) : (
          <div className="space-y-2">
            {dayAppts.map((a) => (
              <div key={a.id} className="rounded-lg border border-navy/10 p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold text-ink">{a.patient_name}</p>
                    <p className="text-xs text-muted-foreground">{fmtTime(a.scheduled_at)} · {a.service || "General"}</p>
                  </div>
                  {statusBadge(a.status)}
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  {(a.status === "scheduled" || a.status === "confirmed") && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "checked_in")} className="h-7 border-navy/20 text-xs text-navy">
                      <LogIn className="mr-1 h-3 w-3" /> Check In
                    </Button>
                  )}
                  {a.status === "checked_in" && (
                    <Button size="sm" onClick={() => onStartConsultation(a.id)} className="h-7 bg-navy text-xs text-white hover:bg-navy-hover">
                      <Stethoscope className="mr-1 h-3 w-3" /> Start
                    </Button>
                  )}
                  {a.status === "in_consultation" && (
                    <Button size="sm" variant="outline" onClick={() => onStartConsultation(a.id)} className="h-7 border-navy/20 text-xs text-navy">
                      <Stethoscope className="mr-1 h-3 w-3" /> Resume
                    </Button>
                  )}
                  {(a.status === "checked_out" || a.status === "completed") && (
                    <span className="text-xs font-medium text-cyan-700">At checkout</span>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onSelect(a)} className="h-7 text-xs text-muted-foreground">
                    Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── Day calendar ───────────────────────── */

// Assign each appointment a lane so overlapping ones render side-by-side.
// Returns [{ appt, start, end, lane, lanes }] where `lanes` is the width
// divisor for that appointment's overlap cluster.
function layoutColumnAppts(appts) {
  const items = appts
    .map((a) => {
      const s = new Date(a.scheduled_at);
      const startMin = s.getHours() * 60 + s.getMinutes();
      return { appt: a, start: startMin, end: startMin + (a.duration_minutes || 60) };
    })
    .sort((x, y) => x.start - y.start || x.end - y.end);

  // Greedy lane assignment within overlap clusters.
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = Math.max(...cluster.map((c) => c.lane + 1), 1);
    cluster.forEach((c) => (c.lanes = lanes));
    cluster = [];
  };
  const laneEnds = []; // end time per lane in the active cluster
  for (const it of items) {
    if (it.start >= clusterEnd && cluster.length) {
      flush();
      laneEnds.length = 0;
    }
    let lane = laneEnds.findIndex((e) => e <= it.start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); }
    else laneEnds[lane] = it.end;
    it.lane = lane;
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  if (cluster.length) flush();
  return items;
}

function DayCalendar({ date, setDate, appointments, doctors, availability, canEditAvailability, onEditAvailability, onSelect, onNew }) {
  const dayAppts = useMemo(() => {
    return appointments.filter((a) => (a.scheduled_at || "").slice(0, 10) === date);
  }, [appointments, date]);

  const shiftDay = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    setDate(`${yyyy}-${mm}-${dd}`);
  };

  // Visible window fits the day's working hours across all shown doctors, then
  // expands to include any out-of-hours appointments so nothing is hidden.
  const { DAY_START_HOUR, DAY_END_HOUR } = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    doctors.forEach((doc) => {
      const dh = dayHours(availability?.[doc.id], date);
      if (dh) { lo = Math.min(lo, dh.start); hi = Math.max(hi, dh.end); }
    });
    // No working hours found (all off / no availability) → fallback window.
    if (lo === Infinity) { lo = FALLBACK_START * 60; hi = FALLBACK_END * 60; }
    // Include appointments that fall outside working hours.
    dayAppts.forEach((a) => {
      const s = new Date(a.scheduled_at);
      const startMin = s.getHours() * 60 + s.getMinutes();
      lo = Math.min(lo, startMin);
      hi = Math.max(hi, startMin + (a.duration_minutes || 60));
    });
    // Snap to whole hours, clamp to outer bounds.
    let startH = Math.max(MIN_HOUR, Math.floor(lo / 60));
    let endH = Math.min(MAX_HOUR, Math.ceil(hi / 60));
    if (endH <= startH) endH = startH + 1;
    return { DAY_START_HOUR: startH, DAY_END_HOUR: endH };
  }, [doctors, availability, date, dayAppts]);

  const hours = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);
  const totalMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const gridHeight = totalMinutes * PX_PER_MIN;

  const fmtHour = (h) => {
    const ampm = h < 12 ? "AM" : "PM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:00 ${ampm}`;
  };
  const fmtRange = (start, mins) => {
    const s = new Date(start);
    const e = new Date(s.getTime() + mins * 60000);
    const t = (d) => {
      const h = d.getHours();
      const hr = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? "AM" : "PM";
      return `${hr}:${String(d.getMinutes()).padStart(2, "0")} ${ampm}`;
    };
    return `${t(s)} – ${t(e)}`;
  };

  // Slot index (0-based, 15-min steps from DAY_START_HOUR) → local datetime / label.
  const slotDate = (i) => {
    const d = new Date(date + "T00:00:00");
    d.setHours(DAY_START_HOUR, i * SLOT_MINUTES, 0, 0);
    return d;
  };
  const slotToISO = (i) => slotDate(i).toISOString();
  const slotLabel = (i) => {
    const d = slotDate(i);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const niceDate = new Date(date + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long",
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Schedule</p>
          <h2 className="font-display text-2xl font-semibold text-navy">{niceDate}</h2>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" /> {dayAppts.length} appointment{dayAppts.length !== 1 ? "s" : ""} today
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(-1)} data-testid="appt-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-9" onClick={() => setDate(todayISO())} data-testid="appt-cal-today">Today</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(1)} data-testid="appt-cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value || todayISO())} className="h-9 w-40 bg-white" data-testid="appt-cal-date" />
        </div>
      </div>

      {doctors.length === 0 ? (
        <div className="rounded-xl border border-navy/10 bg-white p-12 text-center text-muted-foreground">No staff to display.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-navy/10 bg-white shadow-sm">
          <div className="flex min-w-max">
            {/* Time gutter */}
            <div className="w-16 flex-shrink-0 border-r border-navy/10">
              <div className="h-12 border-b border-navy/10" />
              <div className="relative" style={{ height: gridHeight }}>
                {hours.map((h, i) => (
                  <div key={h} className="absolute left-0 right-1 text-right text-[11px] text-muted-foreground"
                    style={{ top: i * 60 * PX_PER_MIN - 6 }}>
                    {fmtHour(h)}
                  </div>
                ))}
              </div>
            </div>

            {/* Staff columns */}
            {doctors.map((doc) => {
              const colAppts = dayAppts.filter((a) => a.doctor_id === doc.id);
              const offRanges = unavailableRanges(availability?.[doc.id], date, DAY_START_HOUR, DAY_END_HOUR);
              return (
                <div key={doc.id} className="w-64 flex-shrink-0 border-r border-navy/10 last:border-r-0" data-testid={`appt-cal-col-${doc.id}`}>
                  {/* Column header */}
                  <div className="flex h-12 items-center justify-between border-b border-navy/10 px-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-gold" />
                      <span className="text-sm font-semibold text-navy">{doc.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{(doc.role || "").replace("_", " ")}</span>
                      {canEditAvailability && (
                        <button onClick={() => onEditAvailability(doc)} title="Edit availability"
                          className="rounded p-0.5 text-muted-foreground hover:bg-nude hover:text-navy" data-testid={`appt-edit-avail-${doc.id}`}>
                          <Clock className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Slots + appointment blocks */}
                  <div className="relative" style={{ height: gridHeight }}>
                    {/* Unavailable time (outside hours / break / day off) — shaded, non-interactive */}
                    {offRanges.map((r, idx) => (
                      <div key={`off-${idx}`} className="pointer-events-none absolute left-0 right-0 bg-navy/[0.06]"
                        style={{
                          top: (r.from - DAY_START_HOUR * 60) * PX_PER_MIN,
                          height: (r.to - r.from) * PX_PER_MIN,
                          backgroundImage: "repeating-linear-gradient(45deg, rgba(27,58,92,0.05) 0, rgba(27,58,92,0.05) 6px, transparent 6px, transparent 12px)",
                        }} />
                    ))}
                    {/* Clickable 15-min slot rows (empty-slot booking).
                        Hour = solid darker, half-hour = medium, quarters = light. */}
                    {Array.from({ length: totalMinutes / SLOT_MINUTES }).map((_, i) => {
                      const min = (i * SLOT_MINUTES) % 60;
                      const lineColor = min === 0 ? "#C9CFD6" : min === 30 ? "#E2E6EB" : "#EEF0F3";
                      return (
                        <button
                          key={i}
                          onClick={() => onNew({ doctor_id: doc.id, scheduled_at: slotToISO(i) })}
                          className="group absolute left-0 right-0 hover:bg-gold/5"
                          style={{
                            top: i * SLOT_MINUTES * PX_PER_MIN,
                            height: SLOT_MINUTES * PX_PER_MIN,
                            borderBottom: `1px solid ${lineColor}`,
                          }}
                          title={`Book ${doc.name} at ${slotLabel(i)}`}
                          data-testid={`appt-cal-slot-${doc.id}-${i}`}
                        >
                          <Plus className="absolute right-1 top-0.5 h-3 w-3 text-gold opacity-0 group-hover:opacity-100" />
                        </button>
                      );
                    })}

                    {layoutColumnAppts(colAppts).map(({ appt: a, lane, lanes }) => {
                      const start = new Date(a.scheduled_at);
                      const minsFromStart = (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes();
                      const dur = a.duration_minutes || 60;
                      if (minsFromStart < 0 || minsFromStart > totalMinutes) return null;
                      // Height matches the real duration so a 15-min slot fills
                      // exactly one slot (no over-spanning). Tiny floor for safety.
                      const h = Math.max(dur * PX_PER_MIN - 2, 26);
                      const c = cardColors(a.status);
                      // Side-by-side lanes for overlapping appointments.
                      const gutterPct = 1.5;
                      const widthPct = (100 - gutterPct * 2) / lanes;
                      const leftPct = gutterPct + lane * widthPct;
                      // Drop the service line on short or narrow cards so the
                      // name + time still fit within the real card height.
                      const showService = lanes === 1 && h >= 64;
                      return (
                        <button
                          key={a.id}
                          onClick={() => onSelect(a)}
                          className="absolute z-10 flex flex-col overflow-hidden rounded-lg text-left shadow-sm ring-1 ring-black/5 transition-shadow hover:z-20 hover:shadow-md"
                          style={{
                            top: minsFromStart * PX_PER_MIN, height: h,
                            left: `${leftPct}%`, width: `${widthPct - 0.5}%`,
                            backgroundColor: c.body, borderLeft: `3px solid ${c.bar}`,
                          }}
                          title={`${a.patient_name} — ${a.service || "No service"} (${fmtRange(a.scheduled_at, dur)})`}
                          data-testid={`appt-cal-card-${a.id}`}
                        >
                          {/* Solid header band */}
                          <div className="px-2 py-0.5" style={{ backgroundColor: c.head }}>
                            <p className="truncate text-[12px] font-semibold leading-tight text-ink">{a.patient_name}</p>
                            {showService && <p className="truncate text-[11px] leading-tight text-ink/60">{a.service || "No service"}</p>}
                            <p className="truncate text-[10px] leading-tight text-ink/55">{fmtRange(a.scheduled_at, dur)}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── List view (original) ───────────────────────── */

// Clickable column header. Shows a neutral sort glyph until active, then an
// up/down arrow for the current direction.
function SortableTh({ label, sortKey, sort, onToggle, testid }) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === -1 ? ArrowDown : ArrowUp;
  return (
    <th className="px-4 py-3">
      <button
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${
          active ? "font-semibold text-navy" : "hover:text-ink"
        }`}
        title={`Sort by ${label.toLowerCase()}`}
        data-testid={testid}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? "" : "opacity-50"}`} />
      </button>
    </th>
  );
}

const APPT_COL_STORAGE_KEY = "dermatrix_appointment_columns";

// Every column the Appointments table can offer, in the picker's "Available"
// order. Mirrors the Leads page's ALL_COLUMNS shape:
//   locked   — always in use; can't be removed or reordered (the row identity)
//   sortKey  — renders a SortableTh over that field instead of a plain header
//   revenue  — dropped entirely when the role can't see money
//   numeric  — right-aligned
// The Action column is NOT here: it's role-gated, not user-configurable.
const APPT_ALL_COLUMNS = [
  { key: "patient_name", label: "Name", locked: true },
  { key: "created_at", label: "Booked", sortKey: "created_at", testid: "appt-sort-booked" },
  { key: "mobile", label: "Phone" },
  { key: "service", label: "Service" },
  { key: "source", label: "Source" },
  { key: "doctor", label: "Doctor" },
  { key: "branch", label: "Branch" },
  { key: "scheduled_date", label: "Appointment Date", sortKey: "scheduled_at", testid: "appt-sort-date" },
  { key: "scheduled_time", label: "Appointment Time" },
  { key: "revenue", label: "Revenue", revenue: true, numeric: true },
  { key: "outstanding", label: "Outstanding", revenue: true, numeric: true },
  { key: "notes", label: "Remark" },
  { key: "status", label: "Status" },
];
const APPT_COL_MAP = Object.fromEntries(APPT_ALL_COLUMNS.map((c) => [c.key, c]));
// Default = the exact layout the page shipped with (Booked first, then Name),
// so nobody's view changes until they open the picker.
const APPT_DEFAULT_VISIBLE_COLS = [
  "created_at", "patient_name", "mobile", "service", "source", "doctor", "branch",
  "scheduled_date", "scheduled_time", "revenue", "outstanding", "notes", "status",
];

/** One <td> for `col` of appointment `a`. `ctx` carries the row's callbacks. */
function renderApptCell(col, a, ctx) {
  const muted = "px-4 py-3 text-muted-foreground";
  switch (col.key) {
    case "patient_name":
      return <td key={col.key} className="px-4 py-3"><p className="font-semibold text-ink">{a.patient_name}</p></td>;
    case "created_at":
      return <td key={col.key} className={`${muted} whitespace-nowrap`}>{formatDate(a.created_at)}</td>;
    case "mobile":
      return <td key={col.key} className={muted}>{maskPhone(a.mobile) || "—"}</td>;
    case "service":
      return <td key={col.key} className={muted}>{a.service || "—"}</td>;
    case "source":
      return <td key={col.key} className={muted}>{a.source || "—"}</td>;
    case "doctor":
      return <td key={col.key} className={muted}>{ctx.docName(a.doctor_id)}</td>;
    case "branch":
      return <td key={col.key} className={muted}>{ctx.branchName(a.branch_id)}</td>;
    case "scheduled_date":
      return <td key={col.key} className={`${muted} whitespace-nowrap`}>{formatDate(a.scheduled_at)}</td>;
    case "scheduled_time":
      return <td key={col.key} className={`${muted} whitespace-nowrap`}>{formatTime(a.scheduled_at)}</td>;
    case "revenue":
      return <td key={col.key} className="px-4 py-3 text-right text-muted-foreground">{a.revenue != null && a.revenue !== "" ? `₹${Number(a.revenue).toLocaleString("en-IN")}` : "—"}</td>;
    case "outstanding":
      return <td key={col.key} className={`px-4 py-3 text-right ${Number(a.outstanding) > 0 ? "font-medium text-red-600" : "text-muted-foreground"}`}>{a.outstanding != null && a.outstanding !== "" ? `₹${Number(a.outstanding).toLocaleString("en-IN")}` : "—"}</td>;
    case "notes":
      return <td key={col.key} className={`${muted} max-w-[200px]`}><span className="line-clamp-2" title={a.notes || ""}>{a.notes || "—"}</span></td>;
    case "status":
      return (
        <td key={col.key} className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {ctx.canChangeStatus ? (
            <Select value={a.status} onValueChange={(v) => {
              // Rescheduling needs a new date/time — open the focused
              // reschedule dialog (date & time only) instead of saving blindly.
              if (v === "rescheduled") { ctx.onReschedule(a); return; }
              // Marking Visited: the patient came in, so capture revenue.
              // Telecallers (no revenue access) just get the plain change.
              if (v === "visited" && ctx.canViewRevenue) { ctx.onVisited(a); return; }
              ctx.updateStatus(a.id, v, a.patient_name);
            }}>
              <SelectTrigger className="h-8 w-[150px] border-none bg-transparent p-0 shadow-none" data-testid={`appointment-status-${a.id}`}>
                <StatusBadge status={a.status} />
              </SelectTrigger>
              <SelectContent>{appointmentStatusOptionsFor(ctx.userRole, a.status).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <StatusBadge status={a.status} />
          )}
        </td>
      );
    default:
      return <td key={col.key} className={muted}>—</td>;
  }
}

function ListView({ filters, setFilter, setDateRange, clearFilters, hasActiveFilters, doctors, branches, filtered, items, docName, branchName, updateStatus, onSelect, onReschedule, onVisited, onStartConsultation, onOpenLead, onViewCalls, userRole, canViewRevenue, actions }) {
  const canChangeStatus = APPT_STATUS_ROLES.includes(userRole);
  // Check-in and consultation happen at the clinic desk, so a telecaller gets
  // the status dropdown and Edit but not those buttons (see CLINIC_ONLY_STATUSES).
  const canDoClinicActions = canChangeStatus && userRole !== "telecaller";

  // Client-side pagination (the whole list is already loaded). Matches the Leads
  // page: a "Per page" selector + Prev/Next, with the table scrolling internally.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Column chooser — same dual-list picker as the Leads page, persisted per
  // browser. Unknown keys are dropped on read so removing a column from
  // APPT_ALL_COLUMNS can't break a saved layout.
  const [colChooserOpen, setColChooserOpen] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const s = localStorage.getItem(APPT_COL_STORAGE_KEY);
      return s ? JSON.parse(s).filter((k) => APPT_COL_MAP[k]) : APPT_DEFAULT_VISIBLE_COLS;
    } catch { return APPT_DEFAULT_VISIBLE_COLS; }
  });
  useEffect(() => { localStorage.setItem(APPT_COL_STORAGE_KEY, JSON.stringify(visibleCols)); }, [visibleCols]);

  // Revenue columns vanish from the table AND the picker for roles that can't
  // see money, rather than showing an option that renders nothing.
  const chooserColumns = useMemo(
    () => APPT_ALL_COLUMNS.filter((c) => !c.revenue || canViewRevenue),
    [canViewRevenue]
  );
  const effectiveCols = useMemo(
    () => visibleCols.map((k) => APPT_COL_MAP[k]).filter((c) => c && (!c.revenue || canViewRevenue)),
    [visibleCols, canViewRevenue]
  );

  // Column sorting: Booked (created_at) and Appointment Date (scheduled_at).
  // First click = newest first, second flips, third clears back to the default
  // server order (appointment date, oldest first). ISO timestamps compare
  // correctly as plain strings.
  const [sort, setSort] = useState(null); // { key, dir: 1 | -1 } | null
  const toggleSort = (key) => {
    setPage(1);
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: -1 };
      if (s.dir === -1) return { key, dir: 1 };
      return null;
    });
  };
  const rows = useMemo(() => {
    if (!sort) return filtered;
    return [...filtered].sort(
      (a, b) => (a[sort.key] || "").localeCompare(b[sort.key] || "") * sort.dir
    );
  }, [filtered, sort]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamp the page whenever filters shrink the result set below the current page.
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);
  const pageItems = rows.slice((page - 1) * pageSize, page * pageSize);
  const colCount = effectiveCols.length + (canChangeStatus ? 1 : 0);

  // Everything renderApptCell needs that isn't the appointment itself.
  const cellCtx = { docName, branchName, updateStatus, onReschedule, onVisited, canChangeStatus, canViewRevenue, userRole };

  // Badge on the "Dates" popover — one per range that's set, so a collapsed
  // filter still announces itself.
  const dateFilterCount =
    (filters.date_from || filters.date_to ? 1 : 0) +
    (filters.booked_from || filters.booked_to ? 1 : 0);
  // Clicking a date input should drop the calendar, not just focus the field.
  const openNativePicker = (e) => { try { e.currentTarget.showPicker?.(); } catch { /* unsupported — the native icon still works */ } };

  return (
    // Fills the height its parent (the viewport-bounded page) leaves after the
    // toolbar, so ONLY the table scrolls internally — the filters and pagination
    // stay put and there's no dead space below the table (same as the Leads page).
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Compact quick-filter row — same sizing/idiom as the Leads page: h-7
          controls, gap-1, and the four date inputs tucked behind a popover so
          the toolbar stays one line on a normal screen. */}
      <div className="flex shrink-0 flex-wrap items-center gap-1">
        <MultiSelect
          className="h-7 w-[104px] px-2 text-xs"
          data-testid="appt-filter-status"
          placeholder="Status"
          options={APPOINTMENT_STATUS.map((s) => ({ value: s.value, label: s.label }))}
          selected={filters.status}
          onChange={(v) => setFilter("status", v)}
        />
        <MultiSelect
          className="h-7 w-[104px] px-2 text-xs"
          data-testid="appt-filter-doctor"
          placeholder="Doctor"
          options={doctors.map((d) => ({ value: d.id, label: d.name }))}
          selected={filters.doctor_id}
          onChange={(v) => setFilter("doctor_id", v)}
        />
        <MultiSelect
          className="h-7 w-[104px] px-2 text-xs"
          data-testid="appt-filter-branch"
          placeholder="Branch"
          options={branches.map((b) => ({ value: b.id, label: b.name }))}
          selected={filters.branch_id}
          onChange={(v) => setFilter("branch_id", v)}
        />
        <MultiSelect
          className="h-7 w-[104px] px-2 text-xs"
          data-testid="appt-filter-service"
          placeholder="Service"
          options={SERVICES.map((s) => ({ value: s, label: s }))}
          selected={filters.service}
          onChange={(v) => setFilter("service", v)}
        />
        {/* Quick ranges — the front desk lives on "today", so it shouldn't cost
            two date pickers to get there. */}
        <div className="inline-flex rounded-lg border border-navy/10 bg-white p-0.5">
          {DATE_PRESETS.map((p) => {
            const range = p.range();
            const active = filters.date_from === range.from && filters.date_to === range.to;
            return (
              <button
                key={p.label}
                onClick={() => setDateRange(range.from, range.to)}
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${active ? "bg-navy text-white" : "text-muted-foreground hover:text-ink"}`}
                data-testid={`appt-range-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        {/* Explicit ranges live behind a popover (Leads' "More" pattern): the
            appointment's own date, and the date it was booked. Four date inputs
            inline ate most of the toolbar. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-7 border-navy/15 px-2 text-xs font-medium text-navy hover:bg-navy/5" data-testid="appt-dates-btn">
              Dates
              {dateFilterCount ? (
                <span className="ml-1 rounded-full bg-gold px-1.5 text-[10px] font-bold text-white">{dateFilterCount}</span>
              ) : <ChevronDown className="ml-0.5 h-3 w-3" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 space-y-3">
            <div>
              <p className="mb-1 text-xs font-semibold text-navy/60">Appointment Date</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-0.5 block text-[10px] text-navy/50">From</label>
                  <Input type="date" value={filters.date_from} max={filters.date_to || undefined}
                    onChange={(e) => setFilter("date_from", e.target.value)}
                    onClick={openNativePicker} onFocus={openNativePicker}
                    className="h-8 cursor-pointer text-xs" data-testid="appt-filter-from" />
                </div>
                <div>
                  <label className="mb-0.5 block text-[10px] text-navy/50">To</label>
                  <Input type="date" value={filters.date_to} min={filters.date_from || undefined}
                    onChange={(e) => setFilter("date_to", e.target.value)}
                    onClick={openNativePicker} onFocus={openNativePicker}
                    className="h-8 cursor-pointer text-xs" data-testid="appt-filter-to" />
                </div>
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-navy/60">Booked Date</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-0.5 block text-[10px] text-navy/50">From</label>
                  <Input type="date" value={filters.booked_from} max={filters.booked_to || undefined}
                    onChange={(e) => setFilter("booked_from", e.target.value)}
                    onClick={openNativePicker} onFocus={openNativePicker}
                    className="h-8 cursor-pointer text-xs" data-testid="appt-filter-booked-from" />
                </div>
                <div>
                  <label className="mb-0.5 block text-[10px] text-navy/50">To</label>
                  <Input type="date" value={filters.booked_to} min={filters.booked_from || undefined}
                    onChange={(e) => setFilter("booked_to", e.target.value)}
                    onClick={openNativePicker} onFocus={openNativePicker}
                    className="h-8 cursor-pointer text-xs" data-testid="appt-filter-booked-to" />
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex h-7 items-center gap-1 rounded-lg px-2 text-xs font-medium text-red-600 hover:bg-red-50"
            data-testid="appt-filter-clear"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
        {/* Columns + the page actions travel together on the right, and wrap as
            one group rather than splitting across lines. */}
        <div className="ml-auto flex items-center gap-1">
          <Button variant="outline" onClick={() => setColChooserOpen(true)} className="h-7 border-navy/15 px-2 text-xs font-medium text-navy hover:bg-navy/5" data-testid="appt-select-columns">
            <SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Columns
          </Button>
          {actions}
        </div>
      </div>

      <ColumnChooserDialog
        open={colChooserOpen}
        onOpenChange={setColChooserOpen}
        columns={chooserColumns}
        visibleCols={visibleCols}
        onApply={setVisibleCols}
      />

      {/* Pagination bar (same layout as the Leads page) */}
      <div className="flex shrink-0 items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {total} of {items.length} appointment{items.length !== 1 ? "s" : ""}
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPage(1); setPageSize(Number(v)); }}>
              <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="appt-per-page"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="appt-prev-page">Previous</Button>
          <span className="text-sm text-ink">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="appt-next-page">Next</Button>
        </div>
      </div>

      {/* Table — fills the remaining height, scrolls both directions (sticky header) */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-20">
              <tr className="border-b bg-cream text-left text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                {effectiveCols.map((col) => (
                  col.sortKey
                    ? <SortableTh key={col.key} label={col.label} sortKey={col.sortKey} sort={sort} onToggle={toggleSort} testid={col.testid} />
                    : <th key={col.key} className={`px-4 py-3 ${col.numeric ? "text-right" : ""}`}>{col.label}</th>
                ))}
                {/* Gated to match the body cell below — otherwise a role without
                    the Action column gets a header with no cells under it. */}
                {canChangeStatus && <th className="px-4 py-3 text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr><td colSpan={colCount} className="px-4 py-12 text-center text-muted-foreground">
                  {hasActiveFilters ? "No appointments match your filters." : "No appointments"}
                </td></tr>
              ) : pageItems.map((a) => {
                // Row tint by status: cancelled / no-show → red, visited /
                // completed → green. Everything else stays neutral.
                const rowTint = APPT_ROW_TINT[a.status] || "hover:bg-nude/30";
                return (
                <tr
                  key={a.id}
                  onClick={() => a.lead_id && onOpenLead(a.lead_id)}
                  className={`border-b last:border-0 ${rowTint} ${a.lead_id ? "cursor-pointer" : ""}`}
                  data-testid={`appointment-${a.id}`}
                >
                  {effectiveCols.map((col) => renderApptCell(col, a, cellCtx))}
                  {canChangeStatus && (
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      {canDoClinicActions && (a.status === "scheduled" || a.status === "confirmed") && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "checked_in")} data-testid={`checkin-${a.id}`}>
                          <LogIn className="mr-1 h-3.5 w-3.5" /> Check in
                        </Button>
                      )}
                      {canDoClinicActions && a.status === "checked_in" && (
                        <Button size="sm" onClick={() => onStartConsultation(a.id)} className="bg-navy text-white hover:bg-navy-hover" data-testid={`start-${a.id}`}>
                          <Stethoscope className="mr-1 h-3.5 w-3.5" /> Start
                        </Button>
                      )}
                      {canDoClinicActions && a.status === "in_consultation" && (
                        <Button size="sm" onClick={() => onStartConsultation(a.id)} className="bg-navy text-white hover:bg-navy-hover" data-testid={`resume-${a.id}`}>
                          <Stethoscope className="mr-1 h-3.5 w-3.5" /> Resume
                        </Button>
                      )}
                      {/* Calls logged from the mobile app — reception's
                          confirmation calls, with recordings. Only shown once
                          there's something to look at. */}
                      {a.call_attempts > 0 && (
                        <Button size="sm" variant="ghost" className="text-navy hover:bg-navy/5"
                          onClick={() => onViewCalls(a)}
                          data-testid={`appointment-calls-${a.id}`}
                          title="Call history &amp; recordings">
                          <Phone className="mr-1 h-3.5 w-3.5" /> {a.call_attempts}
                        </Button>
                      )}
                      {/* Editing is an explicit action now (the row opens the lead). */}
                      <Button size="sm" variant="ghost" className="text-navy hover:bg-navy/5" onClick={() => onSelect(a)} data-testid={`edit-appointment-${a.id}`} title="Edit / update appointment">
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                      </Button>
                    </div>
                  </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Waiting room ───────────────────────── */

function waitedMinutes(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
}
function fmtWait(mins) {
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function WaitingRoom({ waiting, docName, updateStatus, onSelect, onStartConsultation, onVisited, canViewRevenue, userRole, onRefresh }) {
  const navigate = useNavigate();
  // Starting a consultation is a clinic-desk action — telecallers don't get it
  // (they keep Check Out / Visited, which is how a phone-only visit closes out).
  const canDoClinicActions = userRole !== "telecaller";
  // Re-render every 30s so wait times stay fresh — and reload, because this
  // board is a hand-off between two people at two screens. Appointments.jsx does
  // not subscribe to live events, and SSE is unreliable here anyway
  // (events.py is single-process; prod runs 2 replicas x 2 workers), so the
  // doctor pressing Complete would otherwise never reach reception's view.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => { setTick((n) => n + 1); onRefresh?.(); }, 30000);
    return () => clearInterval(t);
  }, [onRefresh]);

  const checkedIn = waiting.filter((a) => a.status === "checked_in")
    .sort((a, b) => (a.checked_in_at || "").localeCompare(b.checked_in_at || ""));
  const inConsult = waiting.filter((a) => a.status === "in_consultation");
  const readyForCheckout = waiting
    .filter((a) => a.status === "checked_out" || a.status === "completed")
    .sort((a, b) => (a.checked_out_at || "").localeCompare(b.checked_out_at || ""));

  const Row = ({ a, waitFrom, accent }) => {
    const mins = waitedMinutes(a[waitFrom]);
    const long = mins >= 20;
    const stage = a.status === "checked_in" ? "waiting"
      : a.status === "in_consultation" ? "in consult" : "at desk";
    return (
      <div className="flex items-center gap-3 border-b border-navy/5 px-4 py-3 last:border-0 hover:bg-nude/30" data-testid={`waiting-${a.id}`}>
        <div className={`h-9 w-1 rounded-full ${accent}`} />
        <PatientAvatar
          patientId={a.patient_id} name={a.patient_name}
          photoPath={a.patient?.photo_path} updatedAt={a.patient?.photo_updated_at} size="sm"
        />
        <button onClick={() => onSelect(a)} className="min-w-0 flex-1 text-left">
          <p className="truncate font-semibold text-ink">
            {a.patient_name}
            {a.patient?.patient_no && (
              <span className="ml-2 font-mono text-[10px] font-normal text-muted-foreground">{a.patient.patient_no}</span>
            )}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {(a.service || "—")} · {docName(a.doctor_id)}{a.mobile ? ` · ${maskPhone(a.mobile)}` : ""}
            {a.status === "checked_in" && (
              <span className={a.has_vitals ? "text-green-700" : "text-amber-700"}>
                {a.has_vitals ? " · vitals ✓" : " · no vitals"}
              </span>
            )}
          </p>
        </button>
        <div className="text-right">
          <p className={`text-sm font-semibold ${long ? "text-red-600" : "text-navy"}`}>{fmtWait(mins)}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{stage}</p>
        </div>
        {canDoClinicActions && a.status === "checked_in" && (
          <Button size="sm" onClick={() => onStartConsultation(a.id)} className="bg-navy text-white hover:bg-navy-hover" data-testid={`waiting-start-${a.id}`}>
            <Stethoscope className="mr-1 h-3.5 w-3.5" /> Start
          </Button>
        )}
        {a.status === "in_consultation" && (
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "checked_out", a.patient_name)}
              className="border-navy/20 text-navy" data-testid={`waiting-checkout-${a.id}`}>
              Check Out
            </Button>
            {/* The one-step path stays: a visit with no doctor involved (or a
                telecaller closing it out) must keep working. */}
            <Button
              size="sm"
              onClick={() => {
                if (canViewRevenue) { onVisited(a); return; }
                updateStatus(a.id, "visited", a.patient_name);
              }}
              className="bg-green-700 text-white hover:bg-green-800"
              data-testid={`waiting-visited-${a.id}`}
            >
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Visited
            </Button>
          </div>
        )}
        {(a.status === "checked_out" || a.status === "completed") && (
          <Button size="sm" onClick={() => navigate(`/checkout/${a.id}`)}
            className="bg-navy text-white hover:bg-navy-hover" data-testid={`waiting-do-checkout-${a.id}`}>
            <Receipt className="mr-1 h-3.5 w-3.5" /> Checkout
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-navy/10 px-4 py-3">
          <h3 className="font-display font-semibold text-navy">Waiting ({checkedIn.length})</h3>
          <Armchair className="h-4 w-4 text-gold" />
        </div>
        {checkedIn.length === 0
          ? <p className="px-4 py-10 text-center text-sm text-muted-foreground">No one waiting.</p>
          : checkedIn.map((a) => <Row key={a.id} a={a} waitFrom="checked_in_at" accent="bg-amber-400" />)}
      </div>
      <div className="rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-navy/10 px-4 py-3">
          <h3 className="font-display font-semibold text-navy">In Consultation ({inConsult.length})</h3>
          <Stethoscope className="h-4 w-4 text-gold" />
        </div>
        {inConsult.length === 0
          ? <p className="px-4 py-10 text-center text-sm text-muted-foreground">No active consultations.</p>
          : inConsult.map((a) => <Row key={a.id} a={a} waitFrom="consultation_started_at" accent="bg-indigo-400" />)}
      </div>
      <div className="rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-navy/10 px-4 py-3">
          <h3 className="font-display font-semibold text-navy">Ready for Checkout ({readyForCheckout.length})</h3>
          <Receipt className="h-4 w-4 text-gold" />
        </div>
        {readyForCheckout.length === 0
          ? <p className="px-4 py-10 text-center text-sm text-muted-foreground">Nobody at the desk.</p>
          : readyForCheckout.map((a) => <Row key={a.id} a={a} waitFrom="checked_out_at" accent="bg-cyan-400" />)}
      </div>
    </div>
  );
}

/* ───────────────────────── Booking dialog ───────────────────────── */

function CreateAppointment({ open, onOpenChange, prefill, doctors, branches, onDone, canViewRevenue }) {
  const { user } = useAuth();
  const empty = { patient_name: "", mobile: "", service: "", doctor_id: "", branch_id: "", scheduled_at: "", duration_minutes: "15", lead_id: "", revenue: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [linkedLead, setLinkedLead] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When opened from an empty calendar slot, pre-fill the staff + time.
  // Branch managers get their branch pre-filled automatically.
  useEffect(() => {
    if (open) {
      setForm({
        ...empty,
        doctor_id: prefill?.doctor_id || "",
        branch_id: user?.branch_ids?.length === 1 ? user.branch_ids[0] : "",
        scheduled_at: prefill?.scheduled_at ? toLocalInput(prefill.scheduled_at) : "",
      });
      setLinkedLead(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  const handleLeadSelect = (lead) => {
    setForm((f) => ({
      ...f,
      lead_id: lead.id,
      patient_name: lead.name || f.patient_name,
      mobile: lead.mobile || f.mobile,
      service: lead.interested_service || f.service,
      branch_id: lead.branch_id || f.branch_id,
    }));
    setLinkedLead(lead);
  };

  const handleLeadClear = () => {
    setForm((f) => ({ ...f, lead_id: "" }));
    setLinkedLead(null);
  };

  const submit = async () => {
    if (!form.patient_name || !form.scheduled_at) { toast.error("Patient name and schedule required"); return; }
    try {
      await api.post("/appointments", { ...form, lead_id: form.lead_id || null, scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: parseInt(form.duration_minutes, 10) || 60,
        doctor_id: form.doctor_id || null, branch_id: form.branch_id || null,
        revenue: form.revenue !== "" ? parseFloat(form.revenue) : null });
      toast.success("Appointment booked");
      setForm(empty); setLinkedLead(null); onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Book Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {linkedLead && (
            <Badge variant="secondary" className="gap-1">
              Linked: {linkedLead.friendly_id || linkedLead.name}
              <button onClick={handleLeadClear} className="ml-1 rounded-sm hover:text-destructive"><X className="h-3 w-3" /></button>
            </Badge>
          )}
          <Field label="Patient Name *">
            <LeadSearchInput value={form.patient_name} onChange={(v) => set("patient_name", v)} onLeadSelect={handleLeadSelect} placeholder="Search leads or type name..." data-testid="appt-name" />
          </Field>
          <Field label="Mobile">
            <LeadSearchInput value={form.mobile} onChange={(v) => set("mobile", v)} onLeadSelect={handleLeadSelect} lockedValue={linkedLead?.mobile} placeholder="Search by mobile..." data-testid="appt-mobile" />
          </Field>
          <Field label="Service">
            <Select value={form.service} onValueChange={(v) => set("service", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Doctor">
              <Select value={form.doctor_id} onValueChange={(v) => set("doctor_id", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Branch">
              <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date & Time *"><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="appt-datetime" /></Field>
            <Field label="Duration (min)">
              <Select value={form.duration_minutes} onValueChange={(v) => set("duration_minutes", v)}>
                <SelectTrigger data-testid="appt-duration"><SelectValue /></SelectTrigger>
                <SelectContent>{["15", "30", "45", "60", "90", "120"].map((m) => <SelectItem key={m} value={m}>{m} min</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {canViewRevenue && <Field label="Revenue"><Input type="number" inputMode="decimal" value={form.revenue} onChange={(e) => set("revenue", e.target.value)} placeholder="₹ 0" data-testid="appt-revenue" /></Field>}
            <Field label="Remark"><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Note / remark" data-testid="appt-remark" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-appointment-btn">Book</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Edit dialog ───────────────────────── */

// ISO string → value for a <input type="datetime-local"> (local time, no TZ suffix).
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditAppointment({ appt, onClose, doctors, branches, onDone, onStartConsultation, canViewRevenue, userRole }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [statusNote, setStatusNote] = useState("");
  // The appointment slot is LOCKED by default so an ordinary edit (revenue, note,
  // status) can't accidentally move the date/time. Changing the slot requires an
  // explicit "Reschedule" — this reveals the picker and flags the intent.
  const [rescheduling, setRescheduling] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (appt) {
      setForm({
        service: appt.service || "",
        doctor_id: appt.doctor_id || "",
        branch_id: appt.branch_id || "",
        scheduled_at: toLocalInput(appt.scheduled_at),
        duration_minutes: String(appt.duration_minutes || 60),
        status: appt.status || "scheduled",
        revenue: appt.revenue != null ? String(appt.revenue) : "",
        outstanding: appt.outstanding != null ? String(appt.outstanding) : "",
        notes: appt.notes || "",
      });
      setRescheduling(false);
      setStatusNote("");
      api.get(`/appointments/${appt.id}/history`).then((r) => setHistory(r.data)).catch(() => {});
    }
  }, [appt]);

  if (!appt || !form) return null;

  const statusChanged = form.status !== appt.status;
  const dateChanged = form.scheduled_at !== toLocalInput(appt.scheduled_at);
  // Choosing the "rescheduled" status, or hitting the Reschedule button, both mean
  // the caller intends to move the slot — and that requires a genuinely NEW time.
  const wantsReschedule = rescheduling || form.status === "rescheduled";
  const rescheduleNeedsDate = wantsReschedule && !dateChanged && appt.status !== "rescheduled";

  const startReschedule = () => {
    setRescheduling(true);
    // Default the status to "rescheduled" when they explicitly reschedule.
    if (form.status !== "rescheduled") set("status", "rescheduled");
  };

  const save = async () => {
    if (rescheduleNeedsDate) {
      toast.error("Pick a new date & time to reschedule");
      return;
    }
    try {
      const body = {
        service: form.service,
        doctor_id: form.doctor_id || null,
        branch_id: form.branch_id || null,
        duration_minutes: parseInt(form.duration_minutes, 10) || 60,
        revenue: form.revenue !== "" ? parseFloat(form.revenue) : null,
        outstanding: form.outstanding !== "" ? parseFloat(form.outstanding) : null,
        notes: form.notes,
      };
      // Only send a (possibly changed) slot when the caller explicitly rescheduled.
      // Otherwise the appointment's date/time is left exactly as it was — an edit
      // of revenue/notes/status can never silently move the appointment.
      if (wantsReschedule) {
        body.scheduled_at = form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null;
      }
      // Only send status when it actually changed — re-sending "visited" on an
      // already-visited appointment reads as a second checkout and is refused.
      if (statusChanged) { body.status = form.status; body.status_note = statusNote; }
      await api.put(`/appointments/${appt.id}`, body);
      toast.success("Appointment updated");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statusLabel = (v) => APPOINTMENT_STATUS.find((s) => s.value === v)?.label || v;

  return (
    <Dialog open={!!appt} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-navy">Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 px-3 py-2">
            <p className="font-semibold text-ink">{appt.patient_name}</p>
            {appt.mobile && <p className="text-xs text-muted-foreground">{maskPhone(appt.mobile)}</p>}
          </div>

          <Field label="Service">
            <Select value={form.service} onValueChange={(v) => set("service", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Doctor">
              <Select value={form.doctor_id} onValueChange={(v) => set("doctor_id", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Branch">
              <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={<span>Date &amp; Time{wantsReschedule && <span className="text-red-500"> *</span>}</span>}>
              {wantsReschedule ? (
                <>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)}
                    className={rescheduleNeedsDate ? "border-red-400 focus-visible:ring-red-400" : ""} data-testid="edit-appt-datetime" />
                  {rescheduleNeedsDate
                    ? <p className="mt-1 text-xs text-red-600">Pick a new date &amp; time to reschedule.</p>
                    : <p className="mt-1 text-xs text-muted-foreground">Was {formatDateTime(appt.scheduled_at)}</p>}
                </>
              ) : (
                // Locked by default — an edit shouldn't accidentally move the slot.
                <div className="flex items-center justify-between gap-2 rounded-md border border-input bg-cream/40 px-3 py-2">
                  <span className="text-sm text-ink">{formatDateTime(appt.scheduled_at)}</span>
                  <Button type="button" size="sm" variant="ghost" className="h-7 shrink-0 px-2 text-xs text-navy hover:bg-navy/5" onClick={startReschedule} data-testid="edit-appt-reschedule">
                    <Clock className="mr-1 h-3.5 w-3.5" /> Reschedule
                  </Button>
                </div>
              )}
            </Field>
            <Field label="Duration (min)">
              <Select value={form.duration_minutes} onValueChange={(v) => set("duration_minutes", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["15", "30", "45", "60", "90", "120"].map((m) => <SelectItem key={m} value={m}>{m} min</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {canViewRevenue && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Revenue"><Input type="number" inputMode="decimal" value={form.revenue} onChange={(e) => set("revenue", e.target.value)} placeholder="₹ 0" data-testid="edit-appt-revenue" /></Field>
              <Field label="Outstanding"><Input type="number" inputMode="decimal" value={form.outstanding} onChange={(e) => set("outstanding", e.target.value)} placeholder="₹ 0" data-testid="edit-appt-outstanding" /></Field>
            </div>
          )}
          <Field label="Remark"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Note / remark" data-testid="edit-appt-notes" /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="edit-appt-status"><SelectValue /></SelectTrigger>
              {/* Rescheduling has its own date-focused dialog — but keep the option
                  available if the appointment is already in the rescheduled state
                  so its value stays selectable and doesn't reset on save. */}
              <SelectContent>{appointmentStatusOptionsFor(userRole, form.status).filter((s) => s.value !== "rescheduled" || form.status === "rescheduled").map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            {statusChanged && (
              <div className="mt-2 space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-medium text-amber-700">
                  Status: {statusLabel(appt.status)} → {statusLabel(form.status)}
                </p>
                <Textarea
                  rows={2}
                  placeholder="Add a note for this status change (optional)..."
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  className="text-xs"
                />
              </div>
            )}
          </Field>

          {/* History */}
          {history.length > 0 && (
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">History</p>
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {history.map((h) => (
                  <div key={h.id} className="rounded bg-cream/40 px-2.5 py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      {h.type === "status_change" ? (
                        <>
                          <span className="font-medium text-navy">{statusLabel(h.from_status)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-medium text-navy">{statusLabel(h.to_status)}</span>
                        </>
                      ) : (
                        <span className="font-medium text-navy">{h.summary || (h.type === "created" ? "Booked" : "Updated")}</span>
                      )}
                      <span className="ml-auto text-muted-foreground">{h.user_name} · {formatDateTime(h.created_at)}</span>
                    </div>
                    {h.note && <p className="mt-0.5 text-muted-foreground italic">{h.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {appt.lead_id && (
            <Button variant="outline" onClick={() => { onClose(); navigate(`/leads/${appt.lead_id}`); }}
              className="border-navy/20 text-navy hover:bg-navy/5" data-testid="open-lead-btn">
              <User className="mr-1.5 h-3.5 w-3.5" /> Open Lead
            </Button>
          )}
          {/* Stays available on `visited` too: gating on checked_out alone would
              make the button vanish the instant reception marks the visit —
              the worst possible moment. */}
          {["checked_out", "completed", "visited"].includes(appt.status) && appt.patient_id && (
            <Button variant="outline" onClick={() => {
              onClose();
              window.location.href = `/checkout/${appt.id}`;
            }} className="border-gold/30 text-gold hover:bg-gold/5">
              <Receipt className="mr-1.5 h-3.5 w-3.5" /> Checkout
            </Button>
          )}
          {appt.status === "checked_in" && onStartConsultation && userRole !== "telecaller" && (
            <Button onClick={() => { onClose(); onStartConsultation(appt.id); }} className="bg-navy text-white hover:bg-navy-hover">
              <Stethoscope className="mr-1.5 h-3.5 w-3.5" /> Start Consultation
            </Button>
          )}
          <Button onClick={save} disabled={rescheduleNeedsDate} className="bg-navy text-white hover:bg-navy-hover" data-testid="update-appointment-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Exported for reuse on the Lead Detail page's Appointments tab.
export function StatusChangeDialog({ data, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setNote(""); }, [data]);

  if (!data) return null;

  const label = APPOINTMENT_STATUS.find((s) => s.value === data.newStatus)?.label || data.newStatus;

  const submit = async () => {
    setSaving(true);
    await onConfirm(note);
    setSaving(false);
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Update Status</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 px-3 py-2.5 text-center">
            {data.apptName && <p className="text-sm font-semibold text-ink">{data.apptName}</p>}
            <p className="text-sm text-navy">
              Changing to <span className="font-bold">{label}</span>
            </p>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Note / Reason (optional)</label>
            <Textarea
              rows={3}
              placeholder="Add a note for this status change..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Updating..." : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Reschedule dialog ───────────────────────── */

// Focused reschedule: shows the patient + current slot and asks only for the
// NEW date & time (plus an optional reason). Sets status = rescheduled and the
// new scheduled_at in one save. Keeps telecallers from wading through the full
// edit form just to move an appointment.
export function RescheduleDialog({ appt, onClose, onDone }) {
  const [newAt, setNewAt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appt) { setNewAt(toLocalInput(appt.scheduled_at)); setNote(""); }
  }, [appt]);

  if (!appt) return null;

  const changed = newAt && newAt !== toLocalInput(appt.scheduled_at);

  const submit = async () => {
    if (!changed) { toast.error("Pick a new date & time to reschedule"); return; }
    setSaving(true);
    try {
      await api.put(`/appointments/${appt.id}`, {
        status: "rescheduled",
        scheduled_at: new Date(newAt).toISOString(),
        status_note: note || "",
      });
      toast.success("Appointment rescheduled");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Reschedule Appointment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 px-3 py-2.5">
            <p className="text-sm font-semibold text-ink">{appt.patient_name}</p>
            <p className="text-xs text-muted-foreground">Current: {formatDateTime(appt.scheduled_at)}</p>
          </div>
          <Field label={<span>New Date &amp; Time <span className="text-red-500">*</span></span>}>
            <Input type="datetime-local" value={newAt} onChange={(e) => setNewAt(e.target.value)}
              className={!changed ? "border-red-400 focus-visible:ring-red-400" : ""} data-testid="reschedule-datetime" autoFocus />
            {!changed && <p className="mt-1 text-xs text-red-600">Choose a slot different from the current one.</p>}
          </Field>
          <Field label="Reason (optional)">
            <Textarea rows={2} placeholder="Why is it being rescheduled?" value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !changed} className="bg-navy text-white hover:bg-navy-hover" data-testid="reschedule-confirm-btn">
            {saving ? "Saving..." : "Reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────── Appointment call history dialog ─────────────────── */

// Calls made ABOUT this appointment — reception confirming the slot, chasing a
// no-show, rescheduling on the phone. These live in `appointment_calls`, NOT the
// lead's call log, so they can't skew the telecaller call/talk-time analytics.
// Logged from the mobile app; this is where they're reviewed.
export function AppointmentCallsDialog({ appt, onClose }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playerCallId, setPlayerCallId] = useState(null);
  const [playerUrl, setPlayerUrl] = useState(null);
  const [loadingRecId, setLoadingRecId] = useState(null);

  useEffect(() => {
    if (!appt) return;
    setLoading(true);
    api.get(`/appointments/${appt.id}/calls`)
      .then((r) => setCalls(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [appt]);

  // Revoke the blob URL on unmount so closing mid-playback doesn't leak it.
  useEffect(() => () => { if (playerUrl) window.URL.revokeObjectURL(playerUrl); },
    [playerUrl]);

  const togglePlayer = async (call) => {
    if (playerCallId === call.id) {
      if (playerUrl) window.URL.revokeObjectURL(playerUrl);
      setPlayerCallId(null);
      setPlayerUrl(null);
      return;
    }
    setLoadingRecId(call.id);
    try {
      const res = await api.get(
        `/appointments/${appt.id}/calls/${call.id}/recording`,
        { responseType: "blob" });
      if (playerUrl) window.URL.revokeObjectURL(playerUrl);
      setPlayerUrl(window.URL.createObjectURL(new Blob([res.data])));
      setPlayerCallId(call.id);
    } catch (e) { toast.error("Could not load recording"); }
    finally { setLoadingRecId(null); }
  };

  const downloadRecording = async (call) => {
    try {
      const res = await api.get(
        `/appointments/${appt.id}/calls/${call.id}/recording`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", call.recording_name || "recording.mp3");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download recording"); }
  };

  const fmtDuration = (s) => {
    if (!s || s <= 0) return "Not connected";
    const m = Math.floor(s / 60);
    return m === 0 ? `${s}s` : `${m}m ${String(s % 60).padStart(2, "0")}s`;
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Call History</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {appt?.patient_name} · {formatDateTime(appt?.scheduled_at)}
          </p>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {loading && <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>}
          {!loading && calls.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No calls logged for this appointment yet.
            </p>
          )}
          {calls.map((c) => (
            <div key={c.id} className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
              <div className="flex items-center gap-3 border-b border-navy/5 px-4 py-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
                  <Phone className="h-4 w-4 text-emerald-600" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-navy">{c.call_type || "Outgoing"}</p>
                  <p className="text-xs text-muted-foreground">{c.user_name || "—"}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Talk time</p>
                  <p className="text-sm text-ink">{fmtDuration(c.duration)}</p>
                </div>
                <div className="hidden shrink-0 text-right sm:block">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">When</p>
                  <p className="text-sm text-ink">{formatDateTime(c.created_at)}</p>
                </div>
              </div>

              {c.notes && (
                <div className="border-b border-navy/5 px-4 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Call notes</p>
                  <p className="mt-1 text-sm text-ink">{c.notes}</p>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 bg-cream/30 px-4 py-2.5">
                <div className="flex flex-wrap items-center gap-6">
                  {c.status_at_call && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status set</p>
                      <p className="text-sm text-ink">
                        {APPOINTMENT_STATUS.find((s) => s.value === c.status_at_call)?.label || c.status_at_call}
                      </p>
                    </div>
                  )}
                  {c.scheduled_at_call && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Appointment on</p>
                      <p className="text-sm text-ink">{formatDateTime(c.scheduled_at_call)}</p>
                    </div>
                  )}
                  {c.follow_up_at_call && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Callback on</p>
                      <p className="text-sm text-ink">{formatDateTime(c.follow_up_at_call)}</p>
                    </div>
                  )}
                </div>
                {c.has_recording && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 border-navy/15 px-2 text-xs text-navy"
                      onClick={() => togglePlayer(c)} data-testid={`appt-call-play-${c.id}`}>
                      {loadingRecId === c.id
                        ? <span className="mr-1 h-3 w-3 animate-spin rounded-full border-2 border-navy border-t-transparent" />
                        : playerCallId === c.id ? <Square className="mr-1 h-3 w-3 text-red-500" /> : <Play className="mr-1 h-3 w-3" />}
                      Recording
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => downloadRecording(c)} title="Download recording">
                      <Download className="h-3.5 w-3.5 text-navy" />
                    </Button>
                  </div>
                )}
              </div>

              {playerCallId === c.id && playerUrl && (
                <audio controls autoPlay src={playerUrl}
                  onEnded={() => { window.URL.revokeObjectURL(playerUrl); setPlayerCallId(null); setPlayerUrl(null); }}
                  className="h-9 w-full border-t border-navy/5" />
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Visited dialog ───────────────────────── */

// Marking an appointment Visited means the patient came in — the moment to
// record what they paid. The outcome + money fields live in VisitedFields so
// the Checkout screen renders the identical thing rather than a fork of it.
export function VisitedDialog({ appt, onClose, onDone }) {
  const [value, setValue] = useState({
    outcome: "converted", reason: "", revenue: "", outstanding: "",
    treatmentId: "", method: "cash",
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    if (!appt) return;
    setValue({
      outcome: "converted",
      reason: "",
      revenue: appt.revenue != null ? String(appt.revenue) : "",
      outstanding: appt.outstanding != null ? String(appt.outstanding) : "",
      treatmentId: appt.treatment_id || "",
      method: "cash",
    });
    setNote("");
    setPackages([]);
    // Only a checked-in appointment has a patient record; before that there are
    // no packages to bill against and the dialog stays exactly as it was.
    if (appt.patient_id) {
      api.get(`/patients/${appt.patient_id}/packages`)
        .then((r) => {
          const open = (r.data.items || []).filter((i) => i.ledger && i.ledger.balance_due > 0);
          setPackages(open);
          if (!appt.treatment_id && open.length === 1) {
            setValue((v) => ({ ...v, treatmentId: open[0].id }));
          }
        })
        .catch(() => {});
    }
  }, [appt]);

  if (!appt) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/appointments/${appt.id}`, visitedPayload(value, { note }));
      toast.success(value.outcome === "not_converted"
        ? "Visited — not converted (kept for follow-up)"
        : "Visited — converted");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Mark Visited</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 px-3 py-2.5">
            <p className="text-sm font-semibold text-ink">{appt.patient_name}</p>
            <p className="text-xs text-muted-foreground">{formatDateTime(appt.scheduled_at)}</p>
          </div>

          <VisitedFields value={value} onChange={setValue} packages={packages} />

          <Field label="Remark (optional)">
            <Textarea rows={2} placeholder="Note / remark" value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="visited-confirm-btn">
            {saving ? "Saving..." : "Mark Visited"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
